import { useState } from 'react';
import { Link } from 'react-router';
import { useLiveQuery } from 'dexie-react-hooks';
import { PageHeader } from '@/components/PageHeader';
import { Button } from '@/components/Button';
import { Banner } from '@/components/Banner';
import { Card, EstadoVacio, Seccion, Dato } from '@/components/Card';
import { Falta } from '@/components/Falta';
import { MoneyField, TextField, SelectField, DateField } from '@/components/Field';
import {
  verCaja,
  registrarMovimiento,
  editarMovimiento,
  eliminarMovimiento,
  categoriasUsadas,
} from '@/db/repos/cash';
import { verResumenDeRango } from '@/db/repos/reports';
import {
  TIPOS_MOVIMIENTO_CAJA,
  ETIQUETA_MOVIMIENTO_CAJA,
  AYUDA_MOVIMIENTO_CAJA,
  CATEGORIAS_GASTO,
  signoDe,
  type TipoMovimientoCaja,
} from '@/domain/cash';
import { ETIQUETA_GRANULARIDAD, rangoDe, type Granularidad } from '@/domain/profit';
import {
  hoy,
  sumarDias,
  sumarMeses,
  formatearMedio,
  formatearCorto,
  etiquetaMes,
  type FechaLocal,
} from '@/domain/dates';
import { formatearCentavos, textoACentavos } from '@/domain/money';
import { mensajeDeError } from '@/db/tx';
import type { CashMovement } from '@/db/types';

const GRANULARIDADES: Granularidad[] = ['dia', 'semana', 'mes'];

type Vista =
  | { modo: 'resumen' }
  | { modo: 'nuevo'; tipo: TipoMovimientoCaja }
  | { modo: 'editar'; movimiento: CashMovement };

/**
 * Caja: cuanto dinero hay de verdad, que no es lo mismo que la ganancia.
 *
 * LA CONFUSION QUE ESTA PANTALLA EXISTE PARA EVITAR
 *
 * "Gane $5,000" y "tengo $5,000" son cosas distintas. La ganancia nace al vender,
 * el dinero llega cuando el cliente paga, y una parte se convierte en despensa en
 * cuanto se vuelve a comprar. Con clientes que pagan cada quince dias, el negocio
 * puede ir ganando mientras la cartera se vacia.
 *
 * Por eso el bloque "donde esta tu dinero" NUNCA presenta un total solo: siempre
 * con sus tres partes, y con "puedes comprar" aparte, que es el unico numero con el
 * que se puede llegar a la tienda.
 *
 * LO QUE NO CAMBIA
 *
 * La formula de la ganancia y las cuentas por cobrar quedan iguales. Esta pantalla
 * se suma al lado y no reemplaza nada.
 */
export function CashPage() {
  const [granularidad, setGranularidad] = useState<Granularidad>('semana');
  const [ancla, setAncla] = useState<FechaLocal>(hoy());
  const [vista, setVista] = useState<Vista>({ modo: 'resumen' });
  const [error, setError] = useState<string | null>(null);

  const rango = rangoDe(granularidad, ancla);

  /**
   * La ganancia entra como DATO desde el motor de ganancias y no se recalcula aqui.
   * Tener dos formas de calcularla es como se acaba con dos cifras distintas.
   */
  const ganancia = useLiveQuery(
    () => verResumenDeRango(rango.desde, rango.hasta),
    [rango.desde, rango.hasta],
  );

  const caja = useLiveQuery(
    () =>
      verCaja(
        rango.desde,
        rango.hasta,
        ganancia === undefined ? null : ganancia.resultadoNetoCentavos,
      ),
    [rango.desde, rango.hasta, ganancia?.resultadoNetoCentavos],
  );

  if (vista.modo === 'nuevo') {
    return (
      <MovimientoForm
        tipo={vista.tipo}
        onListo={() => setVista({ modo: 'resumen' })}
        onCancelar={() => setVista({ modo: 'resumen' })}
      />
    );
  }

  if (vista.modo === 'editar') {
    return (
      <MovimientoForm
        movimiento={vista.movimiento}
        tipo={vista.movimiento.tipo}
        onListo={() => setVista({ modo: 'resumen' })}
        onCancelar={() => setVista({ modo: 'resumen' })}
      />
    );
  }

  function mover(direccion: -1 | 1) {
    if (granularidad === 'dia') setAncla(sumarDias(ancla, direccion));
    else if (granularidad === 'semana') setAncla(sumarDias(ancla, direccion * 7));
    else setAncla(sumarMeses(ancla, direccion));
  }

  const titulo =
    granularidad === 'dia'
      ? formatearMedio(ancla)
      : granularidad === 'semana'
        ? `${formatearCorto(rango.desde)} – ${formatearCorto(rango.hasta)}`
        : etiquetaMes(ancla);

  return (
    <div className="space-y-6">
      <PageHeader titulo="Caja" descripcion="Cuánto dinero tienes de verdad" />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo completar">
          {error}
        </Banner>
      )}

      {/*
        DONDE ESTA TU DINERO va primero, y antes del selector de periodo, porque no
        depende del periodo: es un saldo acumulado. Ponerlo debajo del selector
        haria creer que cambia al mover la semana.
      */}
      {caja !== undefined && (
        <Seccion
          titulo="💰 Dónde está tu dinero hoy"
          descripcion="Las tres son tuyas, pero solo una se puede gastar"
        >
          <Card className={caja.donde.enCajaCentavos < 0 ? 'border-alerta bg-alerta-suave' : ''}>
            <div className="flex items-baseline justify-between gap-3">
              <span className="text-sm font-medium text-texto">En caja</span>
              <span
                className={`text-2xl font-bold tabular-nums ${
                  caja.donde.enCajaCentavos < 0 ? 'text-alerta' : 'text-marca-fuerte'
                }`}
              >
                {formatearCentavos(caja.donde.enCajaCentavos)}
              </span>
            </div>

            <div className="mt-2 divide-y divide-borde border-t border-borde pt-1">
              {/*
                NO dice "Te deben" a proposito. Esa frase ya significa el por cobrar
                completo en el Tablero, en Ventas y en Pedidos, y usarla aqui para un
                numero mas chico dejaria dos cifras peleadas con las mismas palabras.
              */}
              <Dato
                etiqueta="Entregado sin pagar"
                valor={formatearCentavos(caja.donde.entregadoSinPagarCentavos)}
                detalle="Esa comida ya salió de tu cocina y es cobrable"
              />
              <Dato
                etiqueta="En la despensa"
                valor={formatearCentavos(caja.donde.enInventarioCentavos)}
                detalle="Ya lo pagaste, está en insumos"
              />
            </div>

            <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
              <span className="text-sm text-texto-suave">Valor del negocio</span>
              <span className="font-semibold tabular-nums text-texto">
                {formatearCentavos(caja.donde.totalCentavos)}
              </span>
            </div>
            <p className="mt-1 text-xs text-texto-tenue">
              Ese total NO es efectivo. Es lo que vale tu negocio repartido en tres lugares.
            </p>
          </Card>

          {/*
            Lo apalabrado va en tarjeta APARTE, no como un renglon del valor, porque
            no es valor todavia: es comida que no ha entregado y que en varios casos
            ni esta cocinada. Se muestra porque si sirve saber lo que viene, y se
            deja la suma a la vista para poder cuadrarla con la pantalla de Cobrar.
          */}
          {caja.donde.apalabradoCentavos > 0 && (
            <Card>
              <div className="flex items-baseline justify-between gap-3">
                <span className="text-sm text-texto-suave">Apalabrado, aún no entregas</span>
                <span className="font-semibold tabular-nums text-aviso">
                  {formatearCentavos(caja.donde.apalabradoCentavos)}
                </span>
              </div>
              <p className="mt-1 text-xs text-texto-tenue">
                Pedidos que ya te encargaron. No cuenta como valor todavía: nadie te debe por
                comida que no has entregado, y sus ingredientes ya están contados en la despensa.
              </p>
              <p className="mt-2 border-t border-borde pt-2 text-xs text-texto-tenue">
                Junto con lo entregado sin pagar suma{' '}
                {formatearCentavos(caja.donde.porCobrarCentavos)}, que es el «te deben» que ves en
                el Tablero y en Pedidos.
              </p>
              <div className="mt-3">
                <Link to="/pedidos">
                  <Button variante="fantasma">Ver a quién le cobras</Button>
                </Link>
              </div>
            </Card>
          )}

          {/*
            La pregunta con la que llega a la tienda, con su propia tarjeta. Es el
            unico numero que se puede gastar, y confundirlo con el total es
            exactamente el error que la deja poniendo dinero de su cartera.
          */}
          <Card className="bg-marca-suave">
            <p className="text-sm text-texto-suave">Puedes comprar sin poner dinero tuyo</p>
            <p className="mt-1 text-3xl font-bold tabular-nums text-marca-fuerte">
              {formatearCentavos(caja.donde.puedeComprarCentavos)}
            </p>
            {caja.donde.enCajaCentavos < 0 && (
              <p className="mt-1 text-xs font-medium text-alerta">
                Tu caja está en {formatearCentavos(caja.donde.enCajaCentavos)}, así que cualquier
                compra sale de tu cartera.
              </p>
            )}
          </Card>

          <Card className="bg-superficie-suave">
            <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
              Tu dinero
            </p>
            <dl className="mt-1 divide-y divide-borde">
              <Dato
                etiqueta="Sigue metido en el negocio"
                valor={formatearCentavos(caja.saldo.aportacionNetaCentavos)}
                detalle="Lo que has puesto menos lo que te ha regresado"
              />
              <Dato
                etiqueta="Has retirado para ti"
                valor={formatearCentavos(caja.saldo.retiradoCentavos)}
              />
              <Dato
                etiqueta="Del negocio"
                valor={formatearCentavos(caja.donde.delNegocioCentavos)}
                detalle="Lo que el negocio generó y todavía trae dentro"
              />
            </dl>
          </Card>

          {caja.cobrosSinFecha > 0 && (
            <Banner tono="info" titulo="Hay cobros sin fecha">
              {caja.cobrosSinFecha} pedidos están marcados como pagados pero sin el día del cobro,
              porque son de antes de que la app guardara ese dato. Cuentan en tu caja, pero no
              aparecen en ninguna semana.
            </Banner>
          )}
        </Seccion>
      )}

      {/* --- De aqui para abajo si depende del periodo --- */}

      <Seccion titulo="Movimiento del periodo" descripcion="Lo que entró y salió">
        <div className="flex gap-2">
          {GRANULARIDADES.map((g) => (
            <button
              key={g}
              type="button"
              onClick={() => setGranularidad(g)}
              aria-pressed={granularidad === g}
              className={`min-h-11 flex-1 rounded-xl border px-2 text-sm font-medium ${
                granularidad === g
                  ? 'border-marca bg-marca text-white'
                  : 'border-borde-fuerte bg-superficie text-texto active:bg-superficie-suave'
              }`}
            >
              {ETIQUETA_GRANULARIDAD[g]}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <Button variante="secundario" completo={false} onClick={() => mover(-1)}>
            ‹
          </Button>
          <p className="flex-1 text-center text-sm font-medium capitalize text-texto">{titulo}</p>
          <Button variante="secundario" completo={false} onClick={() => mover(1)}>
            ›
          </Button>
        </div>

        {ancla !== hoy() && (
          <Button variante="fantasma" onClick={() => setAncla(hoy())}>
            Volver a hoy
          </Button>
        )}

        {caja === undefined ? (
          <p className="text-sm text-texto-suave">Cargando…</p>
        ) : (
          <>
            <Card>
              <dl className="divide-y divide-borde">
                <Dato
                  etiqueta="Cobrado a clientes"
                  valor={formatearCentavos(caja.resumen.cobradoCentavos)}
                />
                {caja.resumen.otrosIngresosCentavos > 0 && (
                  <Dato
                    etiqueta="Otro dinero que entró"
                    valor={formatearCentavos(caja.resumen.otrosIngresosCentavos)}
                  />
                )}
                <Dato
                  etiqueta="Compras de insumos"
                  valor={`−${formatearCentavos(caja.resumen.comprasCentavos)}`}
                />
                <Dato
                  etiqueta="Gastos del negocio"
                  valor={`−${formatearCentavos(caja.resumen.gastosCentavos)}`}
                  detalle="Gas, bolsas, transporte"
                />
              </dl>

              {/*
                El flujo del NEGOCIO no cuenta el dinero de ella. Es el numero que
                dice si el negocio se sostiene solo: en rojo, esa semana necesito
                dinero de fuera.
              */}
              <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
                <span className="text-sm font-medium text-texto">Le quedó al negocio</span>
                <span
                  className={`text-xl font-bold tabular-nums ${
                    caja.resumen.flujoDelNegocioCentavos < 0 ? 'text-alerta' : 'text-texto'
                  }`}
                >
                  {formatearCentavos(caja.resumen.flujoDelNegocioCentavos)}
                </span>
              </div>
              <p className="mt-1 text-xs text-texto-tenue">
                Sin contar tu dinero. Si sale en rojo, esa semana el negocio necesitó que
                pusieras.
              </p>

              {(caja.resumen.aportacionesCentavos > 0 ||
                caja.resumen.retirosCentavos > 0 ||
                caja.resumen.devolucionesCentavos > 0) && (
                <dl className="mt-2 divide-y divide-borde border-t border-borde pt-1">
                  {caja.resumen.aportacionesCentavos > 0 && (
                    <Dato
                      etiqueta="Dinero que pusiste"
                      valor={formatearCentavos(caja.resumen.aportacionesCentavos)}
                    />
                  )}
                  {caja.resumen.retirosCentavos > 0 && (
                    <Dato
                      etiqueta="Dinero que sacaste"
                      valor={`−${formatearCentavos(caja.resumen.retirosCentavos)}`}
                    />
                  )}
                  {caja.resumen.devolucionesCentavos > 0 && (
                    <Dato
                      etiqueta="El negocio te regresó"
                      valor={`−${formatearCentavos(caja.resumen.devolucionesCentavos)}`}
                    />
                  )}
                </dl>
              )}

              <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
                <span className="text-sm text-texto-suave">Se movió la caja</span>
                <span
                  className={`font-semibold tabular-nums ${
                    caja.resumen.movimientoNetoCentavos < 0 ? 'text-alerta' : 'text-texto'
                  }`}
                >
                  {formatearCentavos(caja.resumen.movimientoNetoCentavos)}
                </span>
              </div>
            </Card>

            {/*
              La utilidad del negocio SI resta los gastos que Ganancias no ve, porque
              su formula solo conoce el costo de los insumos. Se muestran los dos
              numeros y se dice de donde sale cada uno, para que la diferencia no
              parezca un error.
            */}
            {ganancia !== undefined && ganancia.cuantasVentas > 0 && (
              <Card className="bg-superficie-suave">
                <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
                  Cuánto ganó el negocio
                </p>
                <dl className="mt-1 divide-y divide-borde">
                  <Dato
                    etiqueta="Ganó la comida"
                    valor={formatearCentavos(ganancia.resultadoNetoCentavos)}
                    detalle="Es el número de la pantalla de Ganancias"
                  />
                  <Dato
                    etiqueta="Gastos que no son insumos"
                    valor={`−${formatearCentavos(caja.resumen.gastosCentavos)}`}
                  />
                </dl>
                <div className="mt-2 flex items-baseline justify-between gap-3 border-t border-borde pt-2">
                  <span className="text-sm font-medium text-texto">Ganó el negocio</span>
                  <span className="text-xl font-bold tabular-nums text-texto">
                    {caja.resumen.utilidadDelNegocioCentavos === null
                      ? 'no disponible'
                      : formatearCentavos(caja.resumen.utilidadDelNegocioCentavos)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-texto-tenue">
                  Ganó no es lo mismo que tienes: parte puede estar por cobrar o en la despensa.
                </p>
                <div className="mt-3">
                  <Link to="/ganancias">
                    <Button variante="secundario">Ver ganancias</Button>
                  </Link>
                </div>
              </Card>
            )}

            {caja.resumen.gastosPorCategoria.length > 0 && (
              <Card>
                <p className="text-xs font-semibold uppercase tracking-wide text-texto-tenue">
                  En qué se te fue
                </p>
                <dl className="mt-1 divide-y divide-borde">
                  {caja.resumen.gastosPorCategoria.map((g) => (
                    <Dato
                      key={g.categoria}
                      etiqueta={g.categoria}
                      valor={formatearCentavos(g.montoCentavos)}
                    />
                  ))}
                </dl>
              </Card>
            )}
          </>
        )}
      </Seccion>

      <Seccion titulo="Anotar" descripcion="Lo que la app no puede saber sola">
        <div className="space-y-2">
          {TIPOS_MOVIMIENTO_CAJA.map((tipo) => (
            <Button
              key={tipo}
              variante={tipo === 'gasto' ? 'primario' : 'secundario'}
              onClick={() => setVista({ modo: 'nuevo', tipo })}
            >
              {ETIQUETA_MOVIMIENTO_CAJA[tipo]}
            </Button>
          ))}
        </div>
        <p className="text-xs text-texto-tenue">
          Los cobros y las compras no se anotan aquí: salen solos de tus pedidos y tus tickets.
        </p>
      </Seccion>

      {caja !== undefined && (
        <Seccion titulo="Anotado en este periodo">
          {caja.movimientos.length === 0 ? (
            <EstadoVacio
              titulo="Nada anotado en este periodo"
              descripcion="Aquí van tus gastos, lo que pones y lo que retiras."
            />
          ) : (
            <ul className="space-y-2">
              {caja.movimientos.map((m) => (
                <li key={m.id}>
                  <TarjetaMovimiento
                    movimiento={m}
                    onEditar={() => setVista({ modo: 'editar', movimiento: m })}
                    onError={setError}
                  />
                </li>
              ))}
            </ul>
          )}
        </Seccion>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------

interface PropsTarjeta {
  movimiento: CashMovement;
  onEditar: () => void;
  onError: (mensaje: string | null) => void;
}

function TarjetaMovimiento({ movimiento, onEditar, onError }: PropsTarjeta) {
  const [ocupado, setOcupado] = useState(false);
  const [confirmando, setConfirmando] = useState(false);

  const entra = signoDe(movimiento.tipo) === 1;

  async function borrar() {
    setOcupado(true);
    onError(null);
    try {
      await eliminarMovimiento(movimiento.id);
    } catch (e) {
      onError(mensajeDeError(e));
    } finally {
      setOcupado(false);
    }
  }

  return (
    <Card>
      <div className="flex items-start justify-between gap-3">
        <div className="min-w-0">
          <p className="break-words font-semibold text-texto">{movimiento.concepto}</p>
          <p className="text-sm text-texto-suave">
            {ETIQUETA_MOVIMIENTO_CAJA[movimiento.tipo]}
            {movimiento.categoria !== null && ` · ${movimiento.categoria}`}
          </p>
          <p className="mt-0.5 text-xs text-texto-tenue">{formatearMedio(movimiento.fecha)}</p>
          {movimiento.notas !== null && (
            <p className="mt-1 text-xs italic text-texto-suave">{movimiento.notas}</p>
          )}
        </div>

        <p
          className={`shrink-0 font-bold tabular-nums ${entra ? 'text-exito' : 'text-texto'}`}
        >
          {entra ? '+' : '−'}
          {formatearCentavos(movimiento.montoCentavos)}
        </p>
      </div>

      {confirmando ? (
        <Banner tono="aviso" titulo="¿Borrar este movimiento?">
          <div className="mt-2 flex gap-2">
            <Button variante="secundario" disabled={ocupado} onClick={() => void borrar()}>
              Sí, borrar
            </Button>
            <Button variante="fantasma" completo={false} onClick={() => setConfirmando(false)}>
              No
            </Button>
          </div>
        </Banner>
      ) : (
        <div className="mt-3 flex gap-2">
          <Button variante="fantasma" completo={false} onClick={onEditar}>
            Editar
          </Button>
          <Button variante="fantasma" completo={false} onClick={() => setConfirmando(true)}>
            Borrar
          </Button>
        </div>
      )}
    </Card>
  );
}

// ---------------------------------------------------------------------------

interface PropsForm {
  tipo: TipoMovimientoCaja;
  movimiento?: CashMovement;
  onListo: () => void;
  onCancelar: () => void;
}

function MovimientoForm({ tipo: tipoInicial, movimiento, onListo, onCancelar }: PropsForm) {
  const editando = movimiento !== undefined;

  const [tipo, setTipo] = useState<TipoMovimientoCaja>(tipoInicial);
  const [monto, setMonto] = useState(
    movimiento === undefined ? '' : (movimiento.montoCentavos / 100).toFixed(2),
  );
  const [concepto, setConcepto] = useState(movimiento?.concepto ?? '');
  const [categoria, setCategoria] = useState(movimiento?.categoria ?? '');
  const [fecha, setFecha] = useState<FechaLocal>(movimiento?.fecha ?? hoy());
  const [notas, setNotas] = useState(movimiento?.notas ?? '');
  const [guardando, setGuardando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const usadas = useLiveQuery(() => categoriasUsadas(), []);
  const sugeridas = [...new Set([...(usadas ?? []), ...CATEGORIAS_GASTO])];

  const montoCentavos = textoACentavos(monto);

  // Lista de faltantes y no un booleano: un boton apagado sin motivo no da pistas.
  const faltan: string[] = [];
  if (montoCentavos === null || montoCentavos <= 0) faltan.push('el monto');
  if (concepto.trim() === '') faltan.push('en qué fue');
  const valida = faltan.length === 0;

  async function guardar() {
    if (montoCentavos === null) return;
    setGuardando(true);
    setError(null);
    try {
      if (editando) {
        await editarMovimiento(movimiento.id, {
          tipo,
          montoCentavos,
          concepto,
          fecha,
          categoria: categoria.trim() === '' ? null : categoria,
          notas: notas.trim() === '' ? null : notas,
        });
      } else {
        await registrarMovimiento({
          tipo,
          montoCentavos,
          concepto,
          fecha,
          categoria: categoria.trim() === '' ? null : categoria,
          notas: notas.trim() === '' ? null : notas,
        });
      }
      onListo();
    } catch (e) {
      setError(mensajeDeError(e));
      setGuardando(false);
    }
  }

  return (
    <div className="space-y-5">
      <PageHeader
        titulo={editando ? 'Editar movimiento' : ETIQUETA_MOVIMIENTO_CAJA[tipo]}
        descripcion={AYUDA_MOVIMIENTO_CAJA[tipo]}
      />

      {error !== null && (
        <Banner tono="alerta" titulo="No se pudo guardar">
          {error}
        </Banner>
      )}

      <SelectField
        etiqueta="Qué es"
        valor={tipo}
        onCambio={(v) => setTipo(v as TipoMovimientoCaja)}
        opciones={TIPOS_MOVIMIENTO_CAJA.map((t) => ({
          valor: t,
          etiqueta: ETIQUETA_MOVIMIENTO_CAJA[t],
        }))}
        ayuda={AYUDA_MOVIMIENTO_CAJA[tipo]}
      />

      <MoneyField etiqueta="Cuánto" valor={monto} onCambio={setMonto} autoFocus={!editando} />

      <TextField
        etiqueta="En qué fue"
        valor={concepto}
        onCambio={setConcepto}
        placeholder={tipo === 'gasto' ? 'Gas de la semana' : 'Puse para el pollo'}
        maxLength={120}
      />

      <DateField etiqueta="Cuándo" valor={fecha} onCambio={(v) => setFecha(v as FechaLocal)} />

      {/* La categoria solo tiene sentido en los gastos: en un retiro no significa nada. */}
      {tipo === 'gasto' && (
        <TextField
          etiqueta="Categoría"
          valor={categoria}
          onCambio={setCategoria}
          placeholder="Gas"
          maxLength={60}
          lista={sugeridas}
          ayuda="Para poder ver en qué se te va el dinero. Opcional."
        />
      )}

      <TextField
        etiqueta="Nota"
        valor={notas}
        onCambio={setNotas}
        placeholder="Opcional"
        maxLength={200}
      />

      <div className="space-y-2">
        <Button tamano="grande" disabled={guardando || !valida} onClick={() => void guardar()}>
          {guardando ? 'Guardando…' : 'Guardar'}
        </Button>
        <Falta faltan={faltan} />
        <Button variante="secundario" disabled={guardando} onClick={onCancelar}>
          Cancelar
        </Button>
      </div>
    </div>
  );
}
